import RightChevronIcon from "@/assets/colorable/right_chevron.svg?react";
import FilterChip from "@/components/Common/filter/FilterChip";
import {Tooltip} from "@/components/Common/Tooltip";
import clsxm from "@/lib/clsxm";
import type {TOverflow} from "@/store/types";
import {useId, useLayoutEffect, useRef, useState, type FC, type ReactNode} from "react";
import {useClickAway, useEvent, useKeyPressEvent} from "react-use";

const VIEWPORT_PADDING = 16;
const BASE_HORIZONTAL_SHIFT = -20;

const FilterButton: FC<{
  accessibilityLabel?: string;
  children?: ReactNode | ((close: () => void) => ReactNode);
  onOpen?: () => void;
  dataTestId?: string;
  buttonClassName?: string;
  isBasic?: boolean;
  value: ReactNode;
  isDeleteIconActive?: boolean;
  clear?(): void;
  ClosedIconComponent?: ReactNode;
  OpenedIconComponent?: ReactNode;
  title?: string;
  subTitle?: string;
  className?: string;
  outerClassName?: string;
}> = ({
  accessibilityLabel,
  onOpen,
  buttonClassName,
  dataTestId,
  value,
  ClosedIconComponent,
  OpenedIconComponent,
  title,
  isBasic = true,
  isDeleteIconActive = false,
  clear,
  children,
  subTitle,
  className,
  outerClassName,
}) => {
  const popupId = useId();
  const popupRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  useClickAway(popupRef, () => setIsOpen(false));
  useKeyPressEvent("Escape", () => {
    if (isOpen && accessibilityLabel) buttonRef.current?.focus();
    setIsOpen(false);
  });
  useEvent("scroll", () => setIsOpen(false));
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [buttonHeight, setButtonHeight] = useState<number>(0);
  const [horizontalShift, setHorizontalShift] = useState<number>(BASE_HORIZONTAL_SHIFT);
  const [overflow, setOverflow] = useState<TOverflow>("unspecified");

  useLayoutEffect(() => {
    if (!isOpen || buttonRef.current == null || popupRef.current == null) {
      setHorizontalShift(BASE_HORIZONTAL_SHIFT);
      setOverflow("unspecified");
      return;
    }

    const buttonRect = buttonRef.current.getBoundingClientRect();
    setButtonHeight(buttonRect.height);

    const popupWidth = popupRef.current.getBoundingClientRect().width;
    const viewportWidth = window.innerWidth;

    const wouldOverflowRight = buttonRect.left + BASE_HORIZONTAL_SHIFT + popupWidth > viewportWidth - VIEWPORT_PADDING;

    if (wouldOverflowRight) {
      setHorizontalShift(buttonRect.width - popupWidth - BASE_HORIZONTAL_SHIFT);
      setOverflow("right");
    } else {
      setHorizontalShift(BASE_HORIZONTAL_SHIFT);
      setOverflow("unspecified");
    }
  }, [isOpen]);

  return (
    <div
      className={clsxm(
        "divide-neutral-white-dark relative rounded-sm transition-all duration-150",
        accessibilityLabel && "max-w-full",
        outerClassName,
      )}
      data-testid={dataTestId}>
      <div
        ref={buttonRef}
        role={accessibilityLabel ? "button" : undefined}
        aria-label={accessibilityLabel}
        aria-expanded={accessibilityLabel ? isOpen : undefined}
        aria-controls={accessibilityLabel && isOpen ? popupId : undefined}
        aria-haspopup={accessibilityLabel ? "dialog" : undefined}
        tabIndex={accessibilityLabel ? 0 : undefined}
        onKeyDown={
          accessibilityLabel
            ? event => {
                if (event.target !== event.currentTarget) return;
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  if (!isOpen) onOpen?.();
                  setIsOpen(!isOpen);
                }
              }
            : undefined
        }
        onClick={() => {
          if (!isOpen) onOpen?.();
          setIsOpen(!isOpen);
        }}
        className={clsxm(
          "border-neutral-grey-light-dark flex h-10 shrink-0 cursor-pointer flex-row items-center justify-between gap-x-4 rounded-sm border bg-white px-3 py-1",
          accessibilityLabel && "max-w-full",
          buttonClassName,
        )}>
        <div className={clsxm("flex h-full flex-row items-center", accessibilityLabel && "min-w-0")}>
          <div className="flex items-center">
            {title ? (
              <span
                className={
                  "shrink-0 pr-2 text-sm whitespace-nowrap " +
                  (isBasic ? "text-neutral-grey-dark" : "text-neutral-black")
                }>
                {title}
              </span>
            ) : (
              <>
                <div
                  className="mr-2 flex size-6 items-center justify-center"
                  data-tip
                  data-tooltip-id={`filter-${subTitle}`}>
                  {ClosedIconComponent}
                </div>
                <Tooltip id={`filter-${subTitle}`} place="bottom" className="z-1000" variant="dark">
                  {subTitle}
                </Tooltip>
              </>
            )}
          </div>
          <div className="bg-neutral-grey-light-dark h-full w-px" />
          {isDeleteIconActive ? (
            <FilterChip
              className="ml-2"
              label={value}
              onRemove={e => {
                if (isDeleteIconActive) {
                  e.stopPropagation();
                  clear!();
                }
              }}
            />
          ) : (
            <span className={clsxm("pl-2 text-sm font-semibold whitespace-nowrap", accessibilityLabel && "truncate")}>
              {value}
            </span>
          )}
        </div>
        <RightChevronIcon className="text-neutral-black h-3 rotate-90" />
      </div>

      {isOpen && (
        <div
          ref={popupRef}
          id={accessibilityLabel ? popupId : undefined}
          role={accessibilityLabel ? "dialog" : undefined}
          aria-label={accessibilityLabel}
          style={{
            transform: `translate(${horizontalShift}px, ${-buttonHeight - 20}px)`,
            display: "flex",
            flexWrap: "wrap",
          }}
          className="absolute z-1000 flex flex-col items-start rounded-sm bg-white p-5 shadow-sm">
          <div
            className={clsxm(
              "border-primary-dark-solid text-primary-dark-solid mb-5 flex h-10 cursor-pointer items-center justify-between gap-x-4 rounded-sm border px-3 py-1",
              overflow === "right" && "self-end",
              buttonClassName,
            )}
            onClick={() => {
              setIsOpen(!isOpen);
            }}>
            <div className="flex flex-row items-center">
              <div className="flex grow-0 flex-row items-center justify-center">
                {title ? (
                  <span className="text-primary-dark-solid pr-2 text-sm whitespace-nowrap">{title}</span>
                ) : (
                  <div className="mr-2 flex h-4 w-6 items-center justify-center">{OpenedIconComponent}</div>
                )}
              </div>
              <div className="bg-neutral-grey-light-dark w-px shrink-0 self-stretch" />
              <FilterChip
                className="ml-2"
                label={value}
                onRemove={e => {
                  if (isDeleteIconActive) {
                    e.stopPropagation();
                    clear!();
                  }
                }}
              />
            </div>
            <RightChevronIcon className="text-primary-dark-solid h-3 -rotate-90" />
          </div>
          {subTitle && <span className="mb-2 font-semibold">{subTitle}</span>}
          <div className={clsxm("w-max", className)}>
            {typeof children === "function"
              ? children(() => {
                  setIsOpen(false);
                  buttonRef.current?.focus();
                })
              : children}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterButton;
